// Global toast/notification system.
// Provides a ToastProvider (mount once near the app root), a useToast hook
// for components, and a toast() function for use outside React trees.
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';

export type ToastKind = 'info' | 'success' | 'error' | 'warning';

export interface ToastMessage {
  id: number;
  message: string;
  kind: ToastKind;
}

interface ToastContextValue {
  addToast: (message: string, kind?: ToastKind) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

let _addToast: ((message: string, kind?: ToastKind) => void) | null = null;

/** Imperative toast for use outside React (event handlers, async functions). */
export function toast(message: string, kind: ToastKind = 'info'): void {
  _addToast?.(message, kind);
}

export function useToast(): (message: string, kind?: ToastKind) => void {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside ToastProvider');
  return ctx.addToast;
}

const KIND_COLOR: Record<ToastKind, string> = {
  info: 'var(--ss-text)',
  success: 'var(--ss-success)',
  error: 'var(--ss-danger)',
  warning: 'var(--ss-warning)',
};

const KIND_BG: Record<ToastKind, string> = {
  info: 'var(--ss-panel-strong)',
  success: 'color-mix(in srgb, var(--ss-success) 12%, var(--ss-panel-strong))',
  error: 'color-mix(in srgb, var(--ss-danger) 12%, var(--ss-panel-strong))',
  warning: 'color-mix(in srgb, var(--ss-warning) 12%, var(--ss-panel-strong))',
};

const TOAST_DURATION_MS = 4500;
// A burst of near-identical toasts (e.g. switching editor modes quickly,
// each failing to load its own saved document) used to stack without limit,
// covering the whole panel. Docking to the newest few keeps that burst
// readable instead of a wall of text; nothing is lost, later toasts just
// evict earlier ones sooner than their own timer would have.
const MAX_VISIBLE_TOASTS = 3;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  const counterRef = useRef(0);

  const addToast = useCallback((message: string, kind: ToastKind = 'info') => {
    const id = ++counterRef.current;
    setToasts((prev) => {
      // An identical message already showing (e.g. a rapid double-click, or
      // the same async failure firing twice) just refreshes rather than
      // adding a second, visually identical toast.
      if (prev.some((t) => t.message === message && t.kind === kind)) return prev;
      const next = [...prev, { id, message, kind }];
      return next.length > MAX_VISIBLE_TOASTS ? next.slice(next.length - MAX_VISIBLE_TOASTS) : next;
    });
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, TOAST_DURATION_MS);
  }, []);

  // Expose imperatively for non-hook callers.
  useEffect(() => {
    _addToast = addToast;
    return () => {
      _addToast = null;
    };
  }, [addToast]);

  return (
    <ToastContext.Provider value={{ addToast }}>
      {children}
      {toasts.length > 0 && (
        <div
          aria-live="polite"
          aria-atomic="false"
          style={{
            position: 'fixed',
            bottom: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 8,
            zIndex: 9999,
            pointerEvents: 'none',
          }}
        >
          {toasts.map((t) => (
            <div
              key={t.id}
              role="status"
              style={{
                background: KIND_BG[t.kind],
                border: '1px solid var(--ss-border)',
                borderRadius: 10,
                padding: '10px 18px',
                fontSize: 13,
                fontFamily: 'var(--ss-sans)',
                fontWeight: 500,
                color: KIND_COLOR[t.kind],
                boxShadow: '0 8px 32px rgba(0,0,0,0.28)',
                backdropFilter: 'blur(12px)',
                whiteSpace: 'pre-line',
                maxWidth: 400,
                textAlign: 'center',
              }}
            >
              {t.message}
            </div>
          ))}
        </div>
      )}
    </ToastContext.Provider>
  );
}
